/**
 * Media Library Utilities
 *
 * Helper functions and constants used throughout the Media Library component.
 */

import { MediaType, MediaTypeCard } from '../types';
import { CubeIcon, ImageIcon, VideoIcon, SoundIcon, ImageTrackingIcon } from '../../../icons';
import imgImages from '../../../images/media-types/images.png';
import imgVideos from '../../../images/media-types/videos.png';
import imgAudio from '../../../images/media-types/audio.png';
import img3dModels from '../../../images/media-types/3d-models.png';
import imgHdris from '../../../images/media-types/hdris.png';
import imgTextures from '../../../images/media-types/textures.png';
import imgFonts from '../../../images/media-types/fonts.png';
import imgAvatars from '../../../images/media-types/avatars.png';

export const DEFAULT_MEDIA_TYPE_CARDS: MediaTypeCard[] = [
  {
    type: MediaType.Image,
    title: 'Images',
    description: 'Photos, graphics, and image assets',
    previewImage: imgImages,
    isEnabled: true,
  },
  {
    type: MediaType.Video,
    title: 'Videos',
    description: 'Video files for multimedia experiences',
    previewImage: imgVideos,
    isEnabled: true,
  },
  {
    type: MediaType.Audio,
    title: 'Audio',
    description: 'Sound effects, music, and audio clips',
    previewImage: imgAudio,
    isEnabled: true,
  },
  {
    type: MediaType.Model3D,
    title: '3D Models',
    description: 'Import and manage 3D models for your AR experiences',
    previewImage: img3dModels,
    isEnabled: true,
  },
  {
    type: MediaType.Video360,
    title: '360° Videos',
    description: 'Immersive 360-degree video content',
    previewImage: undefined,
    isEnabled: true,
  },
  {
    type: MediaType.Image360,
    title: '360° Images',
    description: 'Panoramic 360-degree images',
    previewImage: undefined,
    isEnabled: true,
  },
  {
    type: MediaType.HDRI,
    title: 'HDRIs',
    description: 'High Dynamic Range images for realistic lighting',
    previewImage: imgHdris,
    isEnabled: true,
  },
  {
    type: MediaType.Texture,
    title: 'Textures',
    description: 'High-quality textures and materials for 3D objects',
    previewImage: imgTextures,
    isEnabled: true,
  },
  {
    type: MediaType.Font,
    title: 'Fonts',
    description: 'Typography and font files for text rendering',
    previewImage: imgFonts,
    isEnabled: true,
  },
  {
    type: MediaType.Avatar,
    title: 'Avatars',
    description: 'Character avatars and humanoid models',
    previewImage: imgAvatars,
    isEnabled: true,
  },
];

export const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  [MediaType.Image]: 'Images',
  [MediaType.Video]: 'Videos',
  [MediaType.Audio]: 'Audio',
  [MediaType.Model3D]: '3D Models',
  [MediaType.Video360]: '360° Videos',
  [MediaType.Image360]: '360° Images',
  [MediaType.HDRI]: 'HDRIs',
  [MediaType.Texture]: 'Textures',
  [MediaType.Font]: 'Fonts',
  [MediaType.Avatar]: 'Avatars',
  [MediaType.Unknown]: 'Unknown',
};

/**
 * Formats file size in human-readable format
 */
export function formatFileSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${Math.round(size * 10) / 10} ${units[unitIndex]}`;
}

/**
 * Formats date in relative format (e.g., "2 days ago")
 */
export function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
}

// Formats date in "DD MMM YYYY" format (e.g., "22 Mar 2025")
export function formatDate(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(dateObj);
}


/**
 * Gets file extension from filename
 */
export function getFileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() || '';
}

/**
 * Converts a string into a URL-safe slug: lowercase, ASCII alphanumerics
 * and hyphens only, with no leading/trailing or repeated hyphens.
 */
export function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Determines if a file type supports thumbnail generation
 */
export function supportsThumbnail(mediaType: MediaType): boolean {
  return [
    MediaType.Image,
    MediaType.Video,
    MediaType.Model3D,
    MediaType.Texture,
    MediaType.HDRI,
    MediaType.Image360,
    MediaType.Video360,
  ].includes(mediaType);
}

/**
 * Generates placeholder thumbnail URL for media types
 */
export function getPlaceholderThumbnail(mediaType: MediaType): string {
  return `/images/placeholders/${mediaType}-placeholder.svg`;
}

/**
 * Get accepted file types for file input based on media type
 */
export default function getAcceptedFileTypes(mediaType?: MediaType | null): string {
  if (!mediaType) return '*/*';

  switch (mediaType) {
    case MediaType.Image:
      return 'image/*';
    case MediaType.Video:
      return 'video/*';
    case MediaType.Audio:
      return 'audio/*';
    case MediaType.Model3D:
      return '.gltf,.glb,.obj,.fbx';
    default:
      return '*/*';
  }
}

/**
 * Determines media type from file extension
 * @param extension - File extension (with or without leading dot)
 * @returns MediaType enum value
 */
export function getMediaTypeFromExtension(extension: string): MediaType {
  const ext = extension.toLowerCase().replace(/^\./, '');

  // Image extensions
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp', 'tiff'].includes(ext)) {
    return MediaType.Image;
  }

  // Video extensions
  if (['mp4', 'webm', 'mov', 'avi', 'mkv', 'flv', 'wmv', 'm4v'].includes(ext)) {
    return MediaType.Video;
  }

  // Audio extensions
  if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a', 'wma'].includes(ext)) {
    return MediaType.Audio;
  }

  // 3D Model extensions
  if (['glb', 'gltf', 'obj', 'fbx', 'usdz', 'stl', 'ply'].includes(ext)) {
    return MediaType.Model3D;
  }

  // 360 Video extensions
  if (['360mp4', '360webm'].includes(ext)) {
    return MediaType.Video360;
  }

  // 360 Image extensions
  if (['360jpg', '360png'].includes(ext)) {
    return MediaType.Image360;
  }

  // HDRI extensions
  if (['hdr', 'exr'].includes(ext)) {
    return MediaType.HDRI;
  }

  // Texture extensions
  if (['tex', 'dds', 'tga', 'psb'].includes(ext)) {
    return MediaType.Texture;
  }

  // Font extensions
  if (['ttf', 'otf', 'woff', 'woff2', 'eot'].includes(ext)) {
    return MediaType.Font;
  }

  return MediaType.Unknown;
}

/**
 * Extract unique categories from assets metadata
 * Categories are stored in asset.getMetadata().categories as an array or comma-separated string
 *
 * @param assets - Array of assets to extract categories from
 * @returns Sorted array of unique category names
 */
export function extractUniqueCategoriesFromAssets(assets: any[]): string[] {
  const categoriesSet = new Set<string>();

  for (const asset of assets) {
    try {
      const metadata = asset.getMetadata?.();
      const categories = metadata?.categories as string[] | string | undefined;

      if (!categories) continue;

      // Handle categories as array
      if (Array.isArray(categories)) {
        categories.forEach((cat: string) => {
          if (typeof cat === 'string' && cat.trim()) {
            categoriesSet.add(cat.trim());
          }
        });
      }
      // Handle categories as comma-separated string
      else if (typeof categories === 'string' && categories.trim()) {
        categories.split(',').forEach((cat: string) => {
          const trimmed = cat.trim();
          if (trimmed) {
            categoriesSet.add(trimmed);
          }
        });
      }

    } catch (error) {
      console.warn('Error extracting categories from asset:', error);
    }
  }

  return Array.from(categoriesSet).sort();
}

export const getFileTypeIcon = (type: MediaType) => {
  switch (type) {
    case MediaType.Model3D:
      return CubeIcon;
    case MediaType.Image:
      return ImageIcon;
    case MediaType.Video:
      return VideoIcon;
    case MediaType.Audio:
      return SoundIcon;
    case MediaType.Image360:
      return ImageTrackingIcon;
    case MediaType.Video360:
      return VideoIcon;
    case MediaType.HDRI:
      return ImageIcon;
    case MediaType.Texture:
      return ImageIcon;
    case MediaType.Font:
      return CubeIcon;
    case MediaType.Avatar:
      return CubeIcon;
    case MediaType.Unknown:
    default:
      return CubeIcon;
  }
};

/**
 * Extracts unique labels from a list of assets
 * Labels are expected to be in asset metadata under a 'labels' property
 *
 * @param assets - Array of assets to extract labels from
 * @returns Array of unique label strings, sorted alphabetically
 */
export function extractUniqueLabels(assets: any[]): string[] {
  if (!assets || assets.length === 0) {
    return [];
  }

  const labelsSet = new Set<string>();

  for (const asset of assets) {
    const labels = asset?.getMetadata?.()?.labels;

    // Skip if no labels array found
    if (!Array.isArray(labels)) continue;

    // Add non-empty labels to set
    for (const label of labels) {
      if (label && typeof label === 'object' && 'uid' in label) {
        labelsSet.add((label as any).uid);
      }
    }
  }

  return Array.from(labelsSet).sort();
}

export const MAX_MODEL_SIZE = 50000000;	// 50 MB
export const MAX_IMAGE_SIZE = 20000000; // 20 MB
export const MAX_VIDEO_SIZE = 500000000; // 500 MB
export const MAX_AUDIO_SIZE = 50000000;	// 50MB

/**
 * Downloads a cross-origin file by fetching it as a blob and triggering a save dialog.
 */
export async function downloadFile(url: string, filename: string): Promise<void> {
  const response = await fetch(url);
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
}